// Shared transfer-action gating + copy, used by every candidate-transfer entry point
// (the quick-app-review headers, the application action menu, and the job-candidates table)
// so the disable logic and tooltip strings live in exactly one place.

// Hard cap on how many times a candidate can be transferred (mirrors the backend
// RecordRelation::MAX_TRANSFER_CHAIN_DEPTH so the FE and BE block at the same point).
export const MAX_TRANSFER_CHAIN_DEPTH = 10;

export const TRANSFER_ACTION_STRINGS = {
  action: 'Transfer to another job',
  alreadyTransferred: 'Candidate has already been transferred',
  singleSelectionOnly:
    'Transfer is only available for single candidate selection',
  maxTransfersReached: `Each candidate can be transferred a maximum of ${MAX_TRANSFER_CHAIN_DEPTH} times`,
} as const;

type TransferApplicationEntry = {
  transferred_in?: unknown;
  transferred_out?: unknown;
};

type TransferActionInfo = {
  applications?: Record<string, TransferApplicationEntry | undefined>;
  history?: unknown[];
} | null;

// Why the transfer action is disabled, or null when it's allowed:
// - 'transferred': the application is a transferred-out tombstone, or it's part of a chain
//   while n-level transfer is off (retains the pre-feature "already transferred" copy).
// - 'limit': n-level is on but the chain has reached MAX_TRANSFER_CHAIN_DEPTH transfers.
export type TransferDisabledReason = 'transferred' | 'limit' | null;

// Single source of truth for whether (and why) the candidate-transfer action is disabled.
// The "out"/"in-chain" states are read from whichever data the caller has — the application
// status or the transfer_info entry — so every entry point shares one definition.
export const getTransferDisabledReason = ({
  application,
  transferInfo,
  tNLevelTransfer,
}: {
  application?: { id?: number | string; status?: string };
  transferInfo?: TransferActionInfo;
  tNLevelTransfer?: boolean;
}): TransferDisabledReason => {
  const entry = transferInfo?.applications?.[String(application?.id)];
  const isTransferredOut =
    application?.status === 'transferred' || Boolean(entry?.transferred_out);
  const isInChain =
    Boolean(entry?.transferred_in) || (transferInfo?.history?.length ?? 0) > 0;
  // For the active leaf, history holds every app in the chain (transfers + 1), so
  // length > MAX matches the backend's "already at MAX transfers" block.
  const atLimit =
    (transferInfo?.history?.length ?? 0) > MAX_TRANSFER_CHAIN_DEPTH;

  if (isTransferredOut) return 'transferred';
  if (isInChain && !tNLevelTransfer) return 'transferred';
  if (atLimit) return 'limit';
  return null;
};

// Maps a disabled reason to the tooltip/label shown on the transfer action.
export const transferActionTooltip = (reason: TransferDisabledReason) =>
  reason === 'limit'
    ? TRANSFER_ACTION_STRINGS.maxTransfersReached
    : reason
      ? TRANSFER_ACTION_STRINGS.alreadyTransferred
      : TRANSFER_ACTION_STRINGS.action;
